/**
 * visualContract.js
 *
 * Phase 5C — Shared Visual Contract & validation. Defines the minimum
 * VisualDefinition shape (approved Phase 5B Gate) and the single
 * validation function every Visual component calls before rendering.
 *
 * This module does NOT render anything and does NOT fetch data — it only
 * validates the shape of a VisualDefinition and derives the presentation
 * metadata (x-axis kind, y-axis measure list) that every Visual type needs,
 * from the query + result alone. No Visual component re-derives this
 * itself — this is the ONE place that logic lives.
 */

import { validateQuery, getMeasureMetadata } from './analyticalQuery'

export const VISUAL_TYPES = ['kpi', 'table', 'bar', 'line', 'combo']

// ─── VisualDefinition shape (approved Phase 5B Gate) ───────────────────────
// VisualDefinition {
//   type:   'kpi' | 'table' | 'bar' | 'line' | 'combo'
//   query:  AnalyticalQuery         — required for every type EXCEPT
//                                     line with mode:'s_curve' (see below)
//   yAxis?: string[]   — optional; defaults to query.measures (+ any
//                        comparison-derived keys) if omitted
//   mode?:  's_curve'  — ONLY valid when type === 'line'. Per the
//           approved Phase 5D decision, S-Curve is a rendering MODE of
//           Line, not a separate VisualDefinition type. When mode is
//           's_curve', the definition carries `forecastDates: string[]`
//           INSTEAD of `query` — an SCurveResult is a composition of two
//           Adapters (plannedBudgetAdapter + timeSeriesSSOTAdapter), not
//           the output of one AnalyticalQuery, so there is no single
//           `query` to validate the usual way.
// }
//
// Explicitly NOT part of this shape (per approved Phase 5B decisions):
//   xAxis        — derived from query.dimension / result shape, never stored
//   colors       — deferred entirely
//   sort order   — a Bar's default is descending-by-value, applied at
//                  render time only, never persisted
//   Top-N        — not part of Visual Engine v1; belongs to Interactive
//                  Analysis (separate, later phase)
//   interaction
//   filters      — cross-filter state is NOT merged into query.filters
//                  here; see the Interaction State note below

/**
 * Validates a VisualDefinition's shape and its embedded query. Does not
 * touch data — pure shape/registry validation, same posture as
 * validateQuery() itself.
 */
export function validateVisualDefinition(def) {
  const errors = []

  if (!def || typeof def !== 'object') {
    return { valid: false, errors: ['VisualDefinition must be an object'] }
  }

  if (!VISUAL_TYPES.includes(def.type)) {
    errors.push(`Unknown visual type: "${def.type}"`)
  }

  // s_curve mode: a distinct shape (forecastDates, no query) — validated
  // separately and returned early, since none of the query/yAxis/combo
  // rules below apply to it (there is no single AnalyticalQuery to check
  // against; SCurveResult is a composition of two Adapters, not one
  // query's output).
  if (def.type === 'line' && def.mode === 's_curve') {
    if (!Array.isArray(def.forecastDates) || def.forecastDates.length === 0) {
      errors.push('s_curve mode requires a non-empty forecastDates array')
    }
    if (def.query != null) {
      errors.push('s_curve mode does not use `query` — SCurveResult is a composition of plannedBudgetAdapter + timeSeriesSSOTAdapter, not a single AnalyticalQuery result')
    }
    return { valid: errors.length === 0, errors }
  }

  if (def.mode != null && def.mode !== 's_curve') {
    errors.push(`Unknown mode: "${def.mode}" — only "s_curve" is defined, and only for type: 'line'`)
  }
  if (def.mode === 's_curve' && def.type !== 'line') {
    errors.push(`mode: 's_curve' is only valid with type: 'line', not "${def.type}"`)
  }

  const { valid: queryValid, errors: queryErrors } = validateQuery(def.query)
  if (!queryValid) {
    errors.push(...queryErrors.map(e => `query: ${e}`))
  }

  if (def.yAxis != null) {
    if (!Array.isArray(def.yAxis) || def.yAxis.length === 0) {
      errors.push('yAxis, if provided, must be a non-empty array')
    } else if (queryValid) {
      // yAxis entries must resolve to something the query result will
      // actually contain — either a raw measure, or a comparison key
      // this query's comparison will produce.
      const validKeys = new Set(def.query.measures)
      if (def.query.comparison?.type === 'variance') {
        for (const m of def.query.measures) {
          if (m === def.query.comparison.baseline) continue
          validKeys.add(`${m}_vs_${def.query.comparison.baseline}`)
        }
      }
      for (const y of def.yAxis) {
        if (!validKeys.has(y)) {
          errors.push(`yAxis entry "${y}" is not produced by this query's measures/comparison`)
        }
      }
    }
  }

  // combo requires at least 2 distinct y-axis series to be meaningful —
  // a combo with one measure is just a bar or line wearing a different
  // label. Not a hard data-integrity issue, but a real configuration
  // mistake worth catching before render rather than producing a
  // degenerate chart silently.
  if (def.type === 'combo') {
    const resolvedYAxis = def.yAxis || def.query?.measures || []
    if (resolvedYAxis.length < 2) {
      errors.push('combo requires at least 2 yAxis entries — use bar or line for a single measure')
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Resolves the effective yAxis list for a VisualDefinition: the explicit
 * yAxis if provided, otherwise every measure the query requested (plus
 * synthesized comparison keys, if any) — computed once, here, so no
 * Visual component duplicates this fallback logic.
 */
export function resolveYAxis(def) {
  if (def.yAxis) return def.yAxis
  const keys = [...def.query.measures]
  if (def.query.comparison?.type === 'variance') {
    for (const m of def.query.measures) {
      if (m === def.query.comparison.baseline) continue
      keys.push(`${m}_vs_${def.query.comparison.baseline}`)
    }
  }
  return keys
}

/**
 * Determines the x-axis kind from the RESULT shape, never from the
 * VisualDefinition itself (xAxis is never stored — approved Phase 5B
 * decision). A TimeSeriesResult (has `.series`) is always time-based; a
 * QueryResult (has `.rows` directly) is dimension-based if query.dimension
 * is set, or has no meaningful x-axis at all (a single KPI point).
 */
export function resolveXAxisKind(result, query) {
  if (result.series) return 'time'
  if (query.dimension) return 'category'
  return 'none'
}

/**
 * Formats a raw numeric value for display, using the measure's registered
 * format (currency/percentage/ratio) — the ONLY place number formatting
 * logic lives. No Visual component formats a number independently.
 */
export function formatMeasureValue(measureKey, value) {
  const { format } = getMeasureMetadata(measureKey)
  if (value == null || Number.isNaN(value)) return '—'
  switch (format) {
    case 'currency':    return `SAR ${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    case 'percentage':  return `${Number(value).toFixed(1)}%`
    case 'ratio':        return Number(value).toFixed(2)
    default:               return String(value)
  }
}

// ─── Interaction State (approved Phase 5B correction #2) ───────────────────
// Cross-filtering / drill-down NEVER mutate a saved VisualDefinition's
// query.filters directly.
//
// SUPERSEDED (Phase 6B): the full Interaction State layer — drillDown,
// crossFilters, topN, and the hierarchy validation that governs which
// dimension transitions are allowed — now lives in interactionState.js,
// not here. That module's buildEffectiveQuery() handles both drillDown
// (which changes dimension AND filters) and crossFilters (filters only),
// with drill-down transitions validated against DRILL_DOWN_HIERARCHY
// before being applied — this file's original version only merged a
// flat `filters` object with no such validation. Re-exported here for
// any existing import path, but the implementation is not duplicated.
export { buildEffectiveQuery } from './interactionState'
